import React, { useState, useEffect } from 'react';
import { McpApp, ScopeTree } from '../../types';
import { mcpAppsService } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import { loadScopeTree } from '../ScopeFilter';

// Maintenance page for MCP client applications: each row is an app with its
// own API key and per-tool privileges, enforced by /api/mcp.php.

const PRIVILEGES = [
  { key: 'can_query' as const, label: 'Query' },
  { key: 'can_download' as const, label: 'Download' },
  { key: 'can_upload' as const, label: 'Upload' },
  { key: 'can_update' as const, label: 'Update' },
  { key: 'can_consent' as const, label: 'Consent' },
  { key: 'can_lifecycle' as const, label: 'Lifecycle' },
  // Last, and named for what it does rather than for a tool: it is the only
  // privilege here that spends money rather than granting access to data the
  // key was registered to handle.
  { key: 'can_ask' as const, label: 'Ask' },
];

// Receive-only capabilities. Not MCP tools: they let a key POST into
// chat_ingest.php or email_ingest.php, which file what arrives under the key's
// scope. Shown apart from the tool privileges because a receiver key should
// hold one of these and nothing else.
const INGEST = [
  { key: 'can_ingest_chat' as const, label: 'Ingest chat' },
  { key: 'can_ingest_email' as const, label: 'Ingest email' },
];

// The Cloudflare Worker's key, and nothing else: it posts every message with
// the address it was sent to, and the receiver key holding that address files
// it. Needs no scope; refused by the API alongside any other privilege.
const RELAY = { key: 'can_relay_email' as const, label: 'Relay email (the Worker)' };

// Sign-in: people may connect through this app with their console account
// (OAuth) instead of holding its key — how Claude on an iPad or the web
// reaches the server, since a claude.ai connector cannot send a fixed key.
const OAUTH = { key: 'oauth_enabled' as const, label: 'Allow sign-in' };

const NEW_OPTION = '__new__';

interface AppFormState {
  app_name: string;
  version: string;
  vendor: string;
  description: string;
  // How far into the territory > domain hierarchy this key may read.
  // '*' at either level means "all"; enforced by mcp.php as a hard boundary.
  scope_territory: string;
  scope_domain: string;
  // Comma-separated IPs/CIDRs in the form; parsed to an array on save.
  // Empty = the key works from anywhere.
  allowed_from_ips: string;
  contact_email: string;
  can_query: boolean;
  can_download: boolean;
  can_upload: boolean;
  can_update: boolean;
  can_consent: boolean;
  can_lifecycle: boolean;
  can_ask: boolean;
  can_ingest_chat: boolean;
  can_ingest_email: boolean;
  can_relay_email: boolean;
  oauth_enabled: boolean;
  // Email receivers only: comma-separated domains whose staff count as
  // internal. Empty = the key's scope domain.
  internal_domains: string;
  // Email receivers only: the address colleagues forward to.
  receiver_address: string;
}

const emptyForm: AppFormState = {
  app_name: '',
  version: '',
  vendor: '',
  description: '',
  scope_territory: '*',
  scope_domain: '*',
  allowed_from_ips: '',
  contact_email: '',
  can_query: true,
  can_download: false,
  can_upload: false,
  can_update: false,
  can_consent: false,
  can_lifecycle: false,
  can_ask: false,
  can_ingest_chat: false,
  can_ingest_email: false,
  can_relay_email: false,
  oauth_enabled: false,
  internal_domains: '',
  receiver_address: '',
};

const isReceiver = (f: { can_ingest_chat: boolean; can_ingest_email: boolean }) =>
  f.can_ingest_chat || f.can_ingest_email;

const hasToolPrivilege = (f: AppFormState) => PRIVILEGES.some(p => f[p.key]);

// Ticking a receive capability clears the read privileges in the same step.
// Query is on by default (in this form and in the table's column default),
// which is exactly how the first email receiver key ended up able to read:
// a receiver lives in a Worker or on a homeserver, and should hold nothing
// else. Unticking leaves them as they are; they can be re-ticked by hand, and
// the warning then says so.
const withCapability = (
  f: AppFormState, key: 'can_ingest_chat' | 'can_ingest_email' | 'can_relay_email', on: boolean,
): AppFormState => {
  const next = { ...f, [key]: on };
  if (on) {
    PRIVILEGES.forEach(p => { next[p.key] = false; });
    next.oauth_enabled = false;
  }
  return next;
};

const parseIpList = (value: string): string[] =>
  value.split(',').map(s => s.trim()).filter(Boolean);

// The key's reach, as two pulldowns.
//
// Distinct from the pages' ScopeFilter, and deliberately so: there, an empty
// selection means "everything I'm allowed to see" and is sent as an absent
// parameter. Here '*' is a value that gets STORED on the key, and the
// difference between "all territories" and "the 12by9 territory" is a
// permission being granted. Reusing one control for both would make the
// wildcard mean two different things in two places.
const ScopeSelect: React.FC<{
  territory: string;
  domain: string;
  block?: boolean;
  onChange: (territory: string, domain: string) => void;
}> = ({ territory, domain, block, onChange }) => {
  const [tree, setTree] = useState<ScopeTree | null>(null);
  useEffect(() => { loadScopeTree().then(setTree).catch(() => {}); }, []);

  const territories = (tree?.territories ?? []).map(t => t.name);
  const known = tree?.territories.find(t => t.name === territory)?.domains ?? [];
  // A receiver for a new organisation names a place that exists only once
  // the key is saved (the API creates it then). Keep a typed name selectable.
  const domains = domain !== '*' && !known.includes(domain) ? [...known, domain] : known;
  const ask = (what: string): string | null => {
    const v = window.prompt(`New ${what} name`);
    return v && v.trim() ? v.trim().toLowerCase() : null;
  };
  const style: React.CSSProperties = block
    ? { display: 'block', width: '100%', boxSizing: 'border-box' }
    : {};
  const wrap: React.CSSProperties = block
    ? { display: 'block', marginBottom: '4px' }
    : { display: 'inline-flex', alignItems: 'center', gap: '6px' };

  return (
    <>
      <label className="small" style={wrap} title="Which territory this key may read">
        Territory
        <select
          value={territory}
          style={style}
          // Changing the territory resets the domain to the wildcard: a domain
          // name from the old territory would silently deny every call.
          onChange={e => {
            if (e.target.value === NEW_OPTION) {
              const t = ask('territory');
              if (t) onChange(t, '*');
              return;
            }
            onChange(e.target.value, '*');
          }}
        >
          <option value="*">All territories</option>
          {(tree?.territories ?? []).map(t => (
            <option key={t.name} value={t.name}>
              {t.display_name}{t.is_readonly ? ' (read-only)' : ''}
            </option>
          ))}
          {territory !== '*' && !territories.includes(territory) && (
            <option value={territory}>{territory} (new)</option>
          )}
          <option value={NEW_OPTION}>New territory…</option>
        </select>
      </label>
      <label className="small" style={wrap} title="Which domain within that territory">
        Domain
        <select
          value={domain}
          style={style}
          disabled={territory === '*'}
          title={territory === '*' ? 'Pick a territory first' : undefined}
          onChange={e => {
            if (e.target.value === NEW_OPTION) {
              const d = ask('domain');
              if (d) onChange(territory, d);
              return;
            }
            onChange(territory, e.target.value);
          }}
        >
          <option value="*">All domains</option>
          {domains.map(d => <option key={d} value={d}>{known.includes(d) ? d : `${d} (new)`}</option>)}
          <option value={NEW_OPTION}>New domain…</option>
        </select>
      </label>
    </>
  );
};

const formToInput = (form: AppFormState) => ({
  ...form,
  app_name: form.app_name.trim(),
  allowed_from_ips: parseIpList(form.allowed_from_ips),
});

const McpAppsPanel: React.FC = () => {
  const [apps, setApps] = useState<McpApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newApp, setNewApp] = useState<AppFormState>(emptyForm);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editApp, setEditApp] = useState<AppFormState>(emptyForm);
  const [revealedId, setRevealedId] = useState<number | null>(null);
  // Set right after a create or key rotation so the fresh key is impossible
  // to miss; the key also stays retrievable from the row's Show/Copy.
  const [freshKey, setFreshKey] = useState<{ appName: string; key: string } | null>(null);

  const mcpUrl = `${window.location.origin}/api/mcp.php`;
  const connectUrl = `${window.location.origin}/api/connect_claude.php`;

  const fetchApps = async (search: string = appliedSearch) => {
    setLoading(true);
    setError(null);
    try {
      setApps(await mcpAppsService.list(search || undefined));
    } catch (err) {
      setError('Failed to fetch MCP apps');
      console.error('Error fetching MCP apps:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps(appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch]);

  const apiErrorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.error || fallback;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await mcpAppsService.create(formToInput(newApp));
      setFreshKey({ appName: newApp.app_name.trim(), key: result.api_key });
      setNewApp(emptyForm);
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create MCP app'));
    }
  };

  const startEditing = (app: McpApp) => {
    setEditingId(app.id);
    setEditApp({
      app_name: app.app_name,
      version: app.version || '',
      vendor: app.vendor || '',
      description: app.description || '',
      scope_territory: app.scope_territory || '*',
      scope_domain: app.scope_domain || '*',
      allowed_from_ips: app.allowed_from_ips.join(', '),
      contact_email: app.contact_email || '',
      can_query: app.can_query,
      can_download: app.can_download,
      can_upload: app.can_upload,
      can_update: app.can_update,
      can_consent: app.can_consent,
      can_ask: app.can_ask,
      can_lifecycle: app.can_lifecycle,
      can_ingest_chat: app.can_ingest_chat,
      can_ingest_email: app.can_ingest_email,
      can_relay_email: app.can_relay_email,
      oauth_enabled: app.oauth_enabled,
      internal_domains: app.internal_domains || '',
      receiver_address: app.receiver_address || '',
    });
  };

  const saveEditing = async (id: number) => {
    setError(null);
    try {
      await mcpAppsService.update(id, formToInput(editApp));
      setEditingId(null);
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update MCP app'));
    }
  };

  const toggleStatus = async (app: McpApp) => {
    setError(null);
    try {
      await mcpAppsService.update(app.id, { status: app.status === 'active' ? 'disabled' : 'active' });
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update MCP app'));
    }
  };

  const revokeSignIns = async (app: McpApp) => {
    if (!window.confirm(`Sign everyone out of "${app.app_name}"? Each connector must sign in again. `
      + 'To stop new sign-ins as well, untick Allow sign-in.')) {
      return;
    }
    setError(null);
    try {
      await mcpAppsService.revokeSignIns(app.id);
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to sign out'));
    }
  };

  const regenerateKey = async (app: McpApp) => {
    if (!window.confirm(`Generate a new API key for "${app.app_name}"? The current key stops working immediately.`)) {
      return;
    }
    setError(null);
    try {
      const key = await mcpAppsService.regenerateKey(app.id);
      setFreshKey({ appName: app.app_name, key });
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to regenerate key'));
    }
  };

  const deleteApp = async (app: McpApp) => {
    if (!window.confirm(`Permanently delete MCP app "${app.app_name}"? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await mcpAppsService.delete(app.id);
      fetchApps();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete MCP app'));
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access denied (e.g. plain-http origin) — fall back to a
      // prompt the user can copy from manually.
      window.prompt('Copy the API key:', text);
    }
  };

  // "Export": download a small text file with the key and connection info.
  const exportKey = (app: McpApp) => {
    const content = [
      `App:         ${app.app_name}${app.version ? ' v' + app.version : ''}`,
      `Vendor:      ${app.vendor || '—'}`,
      `MCP URL:     ${mcpUrl}`,
      `API key:     ${app.api_key}`,
      `Allowed IPs: ${app.allowed_from_ips.length ? app.allowed_from_ips.join(', ') : 'any IP (unrestricted)'}`,
      '',
      'Connect (Claude Code):',
      `  claude mcp add --transport http vcon ${mcpUrl} --header "Authorization: Bearer ${app.api_key}"`,
      '',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${app.app_name.replace(/[^A-Za-z0-9._-]+/g, '_')}-mcp-key.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maskedKey = (key: string) => `${key.slice(0, 10)}…${key.slice(-4)}`;

  // Labeled edit input: with populated values, placeholders alone can't
  // tell App Name from Vendor from Description — every field gets a
  // visible caption in edit mode.
  const editField = (label: string, key: keyof AppFormState, placeholder = '') => (
    <label className="small" style={{ display: 'block', marginBottom: '4px' }}>
      {label}
      <input
        type="text"
        value={editApp[key] as string}
        placeholder={placeholder}
        style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}
        onChange={e => setEditApp(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </label>
  );

  const privilegeCheckboxes = (
    state: AppFormState,
    setState: React.Dispatch<React.SetStateAction<AppFormState>>,
    idPrefix: string
  ) => (
    <>
      {PRIVILEGES.map(priv => (
        <label key={priv.key} style={{ display: 'block', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            name={`${idPrefix}-${priv.key}`}
            checked={state[priv.key]}
            onChange={e => setState(prev => ({ ...prev, [priv.key]: e.target.checked }))}
          /> {priv.label}
        </label>
      ))}
      <div style={{ marginTop: '6px', fontWeight: 600 }}>Sign-in</div>
      <label style={{ display: 'block', whiteSpace: 'nowrap' }}>
        <input
          type="checkbox"
          name={`${idPrefix}-${OAUTH.key}`}
          checked={state[OAUTH.key]}
          disabled={isReceiver(state) || state.can_relay_email}
          onChange={e => setState(prev => ({ ...prev, [OAUTH.key]: e.target.checked }))}
        /> {OAUTH.label}
      </label>
      <div style={{ marginTop: '6px', fontWeight: 600 }}>Receives</div>
      {INGEST.map(priv => (
        <label key={priv.key} style={{ display: 'block', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            name={`${idPrefix}-${priv.key}`}
            checked={state[priv.key]}
            onChange={e => setState(prev => withCapability(prev, priv.key, e.target.checked))}
          /> {priv.label}
        </label>
      ))}
      <label style={{ display: 'block', whiteSpace: 'nowrap' }}>
        <input
          type="checkbox"
          name={`${idPrefix}-${RELAY.key}`}
          checked={state[RELAY.key]}
          onChange={e => setState(prev => withCapability(prev, RELAY.key, e.target.checked))}
        /> {RELAY.label}
      </label>
      {receiverWarning(state)}
    </>
  );

  // A receiver key lives in a Worker's secrets or on a homeserver; read
  // privileges on it would let whoever holds it read the archive too.
  // Allowed (the API does not refuse it), but said where it is set.
  function receiverWarning(state: AppFormState) {
    if (state.can_relay_email && (hasToolPrivilege(state) || isReceiver(state))) {
      return (
        <div className="small" style={{ color: '#a15c00', whiteSpace: 'normal', maxWidth: '220px' }}>
          A relay key only relays: untick every other privilege and capability.
        </div>
      );
    }
    if (!isReceiver(state) || !hasToolPrivilege(state)) return null;
    return (
      <div className="small" style={{ color: '#a15c00', whiteSpace: 'normal', maxWidth: '220px' }}>
        A receiver key normally has no read privileges: untick the ones above unless this key
        must also read.
      </div>
    );
  }

  if (loading) return <div className="loading">Loading MCP apps...</div>;

  return (
    <div className="settings-panel">
      <h3>MCP Apps</h3>
      {error && <div className="error">{error}</div>}

      <div className="pagination-info">
        MCP endpoint: <span className="monospace">{mcpUrl}</span> (Streamable HTTP, JSON-RPC) —
        clients authenticate with <span className="monospace">Authorization: Bearer &lt;API key&gt;</span>.
        Privileges map to the MCP tools: Query → <span className="monospace">query_vcons</span>,
        Download → <span className="monospace">download_vcon</span> and{' '}
        <span className="monospace">get_dialog_text</span>,
        Upload → <span className="monospace">upload_vcon</span>,
        Update → <span className="monospace">update_vcon</span>,
        Consent → <span className="monospace">record_consent</span> /{' '}
        <span className="monospace">revoke_consent</span>,
        Lifecycle → <span className="monospace">get_lifecycle</span>,
        Ask → <span className="monospace">ask_vcons</span>.
        A key's <span className="monospace">tools/list</span> advertises only what it may call.
        <br />
        <strong>Scope</strong> is a position in the territory &gt; domain hierarchy — the same
        one every listing on this console uses — held as a territory and a domain, either of
        which may be <span className="monospace">*</span> for all. It is an isolation boundary
        rather than a filter: a lookup by UUID or filename outside a key's scope does not
        resolve, and a request naming a territory it cannot see is refused rather than quietly
        narrowed. A territory marked read-only refuses writes from every key regardless of
        privilege, because that constraint is a licence rather than a permission level.
        <br />
        <strong>Ask</strong> is the one privilege that spends money rather than granting access
        to stored data. It is off by default on every key, a question takes 10–45 seconds, and
        it is metered against a daily allowance per key. It cannot read a conversation whose
        parties have not granted the <span className="monospace">analysis</span> consent
        purpose: the override for that belongs to a Data Controller in this console, not to an
        application.
        <br />
        <strong>Receives</strong> makes a key a receiver rather than a reader: <em>Ingest chat</em>{' '}
        lets the chat portal's archive module post to{' '}
        <span className="monospace">chat_ingest.php</span>, <em>Ingest email</em> lets the email
        Worker post forwarded mail to <span className="monospace">email_ingest.php</span>.
        Everything a receiver stores is filed under its scope, so it must name one territory and
        one domain (use <em>New territory…</em> / <em>New domain…</em> for an organisation not
        seen yet). An email receiver's <em>receiver address</em> is the address its colleagues
        forward to (<span className="monospace">acme@12by9.net</span>), and its{' '}
        <em>internal mail domains</em> say whose mail it accepts; anything with a party outside
        them is held. Leave the domains empty to use the key's own domain.{' '}
        <em>Relay email</em> is the Cloudflare Worker's one key: it posts every forwarded message
        with the address it was sent to, and the receiver holding that address files it, so a
        new organisation needs nothing more than its receiver key here. A relay key holds no
        other privilege.
        <br />
        <strong>Allow sign-in</strong> lets people connect through an app with their own console
        account instead of its key (OAuth). That is how Claude on an iPad, on the web or in the
        desktop app's chat reaches this server: on claude.ai, Settings → Connectors → Add custom
        connector, URL <span className="monospace">{mcpUrl}</span>. In Claude Code, one command
        sets it up: <span className="monospace">curl -fsSL {connectUrl} | sh</span>. The person
        signs in, picks one of the apps open to sign-in whose territory and domain lie inside
        their own, and the connector then acts with that app's privileges and scope; what it
        does is logged under the app and their name. The app's IP allowlist applies to its key,
        not to sign-ins (connectors call from their vendor's cloud). Unticking it, disabling the
        app, or narrowing or deactivating the person disconnects them at once;{' '}
        <em>Sign everyone out</em> ends current sign-ins but leaves new ones open.
      </div>

      <form
        onSubmit={e => { e.preventDefault(); setAppliedSearch(searchText.trim()); }}
        className="search-form"
      >
        <input
          name="search"
          type="text"
          placeholder="Search by app name, vendor, description, or IP (partial match)..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <button type="submit">Search</button>
        <button type="button" onClick={() => { setSearchText(''); setAppliedSearch(''); }}>Clear</button>
      </form>

      {freshKey && (
        <div className="pagination-info" style={{ border: '1px solid #ccc', padding: '10px' }}>
          <strong>API key for "{freshKey.appName}":</strong>{' '}
          <span className="monospace">{freshKey.key}</span>{' '}
          <button type="button" onClick={() => copyText(freshKey.key)}>Copy</button>{' '}
          <button type="button" onClick={() => setFreshKey(null)}>Dismiss</button>
        </div>
      )}

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>App</th>
              <th>Status</th>
              <th>Privileges</th>
              <th>API key</th>
              <th>Allowed from / Contact</th>
              <th>Last access</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {apps.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">No MCP apps registered</td>
              </tr>
            ) : (
              apps.map(app => (
                <tr key={app.id}>
                  <td>
                    {editingId === app.id ? (
                      <>
                        {editField('App name', 'app_name')}
                        {editField('Version', 'version')}
                        {editField('Vendor', 'vendor')}
                        {editField('Description', 'description')}
                        <ScopeSelect
                          territory={editApp.scope_territory}
                          domain={editApp.scope_domain}
                          block
                          onChange={(t, d) => setEditApp(prev => ({
                            ...prev, scope_territory: t, scope_domain: d,
                          }))}
                        />
                        {editApp.can_ingest_email &&
                          editField('Receiver address', 'receiver_address', 'e.g. acme@12by9.net')}
                        {editApp.can_ingest_email &&
                          editField('Internal mail domains', 'internal_domains', 'empty = the key\'s domain')}
                      </>
                    ) : (
                      <>
                        <strong>{app.app_name}</strong>{app.version ? ` v${app.version}` : ''}
                        <div className="small">{app.vendor || '—'}</div>
                        {app.description && <div className="small">{app.description}</div>}
                        <div className="small">
                          Scope:{' '}
                          <span className="status status-completed">
                            {app.scope_territory === '*' ? 'all territories' : app.scope_territory}
                            {' / '}
                            {app.scope_domain === '*' ? 'all domains' : app.scope_domain}
                          </span>
                        </div>
                        {app.can_ingest_email && (
                          <>
                            <div className="small" title="The address colleagues forward to">
                              Receives at:{' '}
                              {app.receiver_address
                                ? <span className="monospace">{app.receiver_address}</span>
                                : 'no address (direct posting only)'}
                            </div>
                            <div className="small" title="Whose mail this receiver accepts as internal">
                              Internal mail: {app.internal_domains || `${app.scope_domain} (its domain)`}
                            </div>
                          </>
                        )}
                        <div className="small">
                          Created {formatDateTime(app.created_at)}{app.created_by ? ` by ${app.created_by}` : ''}
                        </div>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`status ${app.status === 'active' ? 'status-completed' : 'status-failed'}`}>
                      {app.status}
                    </span>
                  </td>
                  <td className="small">
                    {editingId === app.id
                      ? privilegeCheckboxes(editApp, setEditApp, `edit-${app.id}`)
                      : (
                        <>
                          {PRIVILEGES.map(priv => (
                            <div key={priv.key} style={{ whiteSpace: 'nowrap' }}>
                              {app[priv.key] ? '✓' : '✗'} {priv.label}
                            </div>
                          ))}
                          {/* Shown unticked too: hidden until ticked, the setting
                              could only be discovered by opening Edit. */}
                          {!isReceiver(app) && !app.can_relay_email && (
                            <>
                              <div style={{ marginTop: '6px', fontWeight: 600 }}>Sign-in</div>
                              <div style={{ whiteSpace: 'nowrap' }}
                                title={app.oauth_enabled
                                  ? 'People holding a live sign-in through this app'
                                  : 'Edit to let people sign in through this app (claude.ai connectors)'}>
                                {app.oauth_enabled
                                  ? `✓ ${OAUTH.label} (${app.oauth_signed_in} signed in)`
                                  : `✗ ${OAUTH.label}`}
                              </div>
                            </>
                          )}
                          {isReceiver(app) && (
                            <>
                              <div style={{ marginTop: '6px', fontWeight: 600 }}>Receives</div>
                              {INGEST.filter(priv => app[priv.key]).map(priv => (
                                <div key={priv.key} style={{ whiteSpace: 'nowrap' }}>✓ {priv.label}</div>
                              ))}
                            </>
                          )}
                          {app.can_relay_email && (
                            <>
                              <div style={{ marginTop: '6px', fontWeight: 600 }}>Receives</div>
                              <div style={{ whiteSpace: 'nowrap' }}>✓ {RELAY.label}</div>
                            </>
                          )}
                        </>
                      )}
                  </td>
                  <td className="monospace small">
                    {revealedId === app.id ? app.api_key : maskedKey(app.api_key)}
                    <div style={{ marginTop: '4px' }}>
                      <button type="button" onClick={() => setRevealedId(revealedId === app.id ? null : app.id)}>
                        {revealedId === app.id ? 'Hide' : 'Show'}
                      </button>{' '}
                      <button type="button" onClick={() => copyText(app.api_key)}>Copy</button>{' '}
                      <button type="button" title="Download the key and connection info as a text file"
                        onClick={() => exportKey(app)}>Export</button>
                    </div>
                  </td>
                  <td className="small">
                    {editingId === app.id ? (
                      <>
                        {editField('Allowed from IPs', 'allowed_from_ips', 'IPs/CIDRs, empty = any IP')}
                        {editField('Contact email', 'contact_email')}
                      </>
                    ) : (
                      <>
                        <div className={app.allowed_from_ips.length > 0 ? 'monospace' : undefined}>
                          {app.allowed_from_ips.length > 0
                            ? app.allowed_from_ips.join(', ')
                            : 'any IP (unrestricted)'}
                        </div>
                        <div>{app.contact_email || '—'}</div>
                      </>
                    )}
                  </td>
                  <td className="small">
                    {app.last_access_at ? (
                      <>
                        {formatDateTime(app.last_access_at)}
                        <div className="monospace">{app.last_access_ip || '—'}</div>
                        <div>{app.access_count} request{app.access_count === 1 ? '' : 's'}</div>
                      </>
                    ) : (
                      'never'
                    )}
                  </td>
                  <td>
                    {editingId === app.id ? (
                      <>
                        <button type="button" onClick={() => saveEditing(app.id)}>Save</button>
                        <button type="button" onClick={() => setEditingId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startEditing(app)}>Edit</button>
                        <button type="button" onClick={() => toggleStatus(app)}>
                          {app.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        <button type="button" onClick={() => regenerateKey(app)}>New key</button>
                        {app.oauth_signed_in > 0 && (
                          <button type="button" onClick={() => revokeSignIns(app)}>Sign everyone out</button>
                        )}
                        {app.status === 'disabled' && (
                          <button type="button" className="danger" onClick={() => deleteApp(app)}>Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3>Register app</h3>
      <form onSubmit={handleCreate} className="search-form settings-form" autoComplete="off">
        <input type="text" placeholder="App name" required value={newApp.app_name}
          onChange={e => setNewApp(prev => ({ ...prev, app_name: e.target.value }))} />
        <input type="text" placeholder="Version" size={8} value={newApp.version}
          onChange={e => setNewApp(prev => ({ ...prev, version: e.target.value }))} />
        <input type="text" placeholder="Vendor" value={newApp.vendor}
          onChange={e => setNewApp(prev => ({ ...prev, vendor: e.target.value }))} />
        <input type="text" placeholder="Allowed from IPs (comma-separated IPs/CIDRs, empty = any IP)" value={newApp.allowed_from_ips}
          onChange={e => setNewApp(prev => ({ ...prev, allowed_from_ips: e.target.value }))} />
        <input type="email" placeholder="Contact email" value={newApp.contact_email}
          onChange={e => setNewApp(prev => ({ ...prev, contact_email: e.target.value }))} />
        <input type="text" placeholder="Description" value={newApp.description}
          onChange={e => setNewApp(prev => ({ ...prev, description: e.target.value }))} />
        <ScopeSelect
          territory={newApp.scope_territory}
          domain={newApp.scope_domain}
          onChange={(t, d) => setNewApp(prev => ({ ...prev, scope_territory: t, scope_domain: d }))}
        />
        {newApp.can_ingest_email && (
          <input type="text" placeholder="Receiver address (e.g. acme@12by9.net)"
            value={newApp.receiver_address}
            onChange={e => setNewApp(prev => ({ ...prev, receiver_address: e.target.value }))} />
        )}
        {newApp.can_ingest_email && (
          <input type="text" placeholder="Internal mail domains (comma-separated; empty = the key's domain)"
            value={newApp.internal_domains}
            onChange={e => setNewApp(prev => ({ ...prev, internal_domains: e.target.value }))} />
        )}
        <span style={{ display: 'inline-flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {PRIVILEGES.map(priv => (
            <label key={priv.key} style={{ whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={newApp[priv.key]}
                onChange={e => setNewApp(prev => ({ ...prev, [priv.key]: e.target.checked }))}
              /> {priv.label}
            </label>
          ))}
          <label style={{ whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={newApp[OAUTH.key]}
              disabled={isReceiver(newApp) || newApp.can_relay_email}
              onChange={e => setNewApp(prev => ({ ...prev, [OAUTH.key]: e.target.checked }))}
            /> {OAUTH.label}
          </label>
          <strong className="small">Receives:</strong>
          {INGEST.map(priv => (
            <label key={priv.key} style={{ whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={newApp[priv.key]}
                onChange={e => setNewApp(prev => withCapability(prev, priv.key, e.target.checked))}
              /> {priv.label}
            </label>
          ))}
          <label style={{ whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={newApp[RELAY.key]}
              onChange={e => setNewApp(prev => withCapability(prev, RELAY.key, e.target.checked))}
            /> {RELAY.label}
          </label>
        </span>
        {receiverWarning(newApp)}
        <button type="submit">Register app</button>
      </form>
    </div>
  );
};

export default McpAppsPanel;
